/**
 * Scans upward from `desiredY` to find a row of pixels that is
 * mostly background (white/near-white), so we never cut through text.
 */
export const findSafeCutY = (canvas, desiredY, searchRange = 80) => {
  const ctx = canvas.getContext("2d");
  const start = Math.max(0, desiredY - searchRange);

  for (let y = desiredY; y >= start; y--) {
    const { data } = ctx.getImageData(0, y, canvas.width, 1);
    let safe = true;
    for (let x = 0; x < canvas.width; x++) {
      const r = data[x * 4];
      const g = data[x * 4 + 1];
      const b = data[x * 4 + 2];
      if (r < 235 || g < 235 || b < 235) {
        safe = false;
        break;
      }
    }
    if (safe) return y;
  }
  return desiredY;
};

/**
 * Injects a white spacer <div> before any element marked with [data-pdf-block]
 * that would straddle a page boundary, pushing the whole block to the next page.
 *
 * Must be called BEFORE html2canvas so the spacers are baked into the canvas.
 * Call removePageBreakSpacers() after the PDF is saved to restore the DOM.
 *
 * @param {HTMLElement} container - The root element being captured (e.g. #pdf-content)
 * @param {number}      pageHeightPx - Page height in DOM pixels (canvas page height / scale)
 */
export const injectPageBreakSpacers = (container, pageHeightPx) => {
  const containerRect = container.getBoundingClientRect();

  container.querySelectorAll("[data-pdf-block]").forEach((block) => {
    // Re-read container rect on each iteration — previous spacers shift subsequent blocks
    const cRect = container.getBoundingClientRect();
    const bRect = block.getBoundingClientRect();

    const blockTop = bRect.top - cRect.top;
    const blockBottom = blockTop + block.offsetHeight;

    const pageStart = Math.floor(blockTop / pageHeightPx);
    const pageEnd = Math.floor((blockBottom - 1) / pageHeightPx);

    // Block straddles a page boundary — push it to the start of the next page
    if (pageStart !== pageEnd) {
      const nextPageTop = (pageStart + 1) * pageHeightPx;
      const spacerHeight = nextPageTop - blockTop;

      // Only insert if the spacer is smaller than a full page (blocks taller than
      // a page can't be saved anyway and we don't want to create blank pages)
      if (spacerHeight > 0 && spacerHeight < pageHeightPx) {
        const spacer = document.createElement("div");
        spacer.setAttribute("data-pdf-spacer", "");
        spacer.style.cssText = `height:${spacerHeight}px;background:#ffffff;display:block;flex-shrink:0;`;
        block.parentNode.insertBefore(spacer, block);
      }
    }
  });

  // Suppress unused-variable warning — containerRect was used for clarity above
  void containerRect;
};

/**
 * Removes all spacers previously injected by injectPageBreakSpacers.
 */
export const removePageBreakSpacers = (container) => {
  container.querySelectorAll("[data-pdf-spacer]").forEach((s) => s.remove());
};

/**
 * Slices `canvas` into A4 pages using smart cut detection and saves the PDF.
 */
export const pdfSliceAndSave = (canvas, pdf, margins, filename) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth - margins.left - margins.right;
  const maxPageHeightPx = ((pageHeight - margins.top - margins.bottom) * canvas.width) / imgWidth;

  let rendered = 0;
  let page = 0;

  while (rendered < canvas.height) {
    const remaining = canvas.height - rendered;
    const desired = Math.min(maxPageHeightPx, remaining);

    // Find a safe cut point (only needed when there's more content after this page)
    const cutY = remaining > desired
      ? findSafeCutY(canvas, rendered + desired)
      : rendered + desired;

    const sliceH = cutY - rendered;
    if (sliceH <= 0) break;

    const pc = document.createElement("canvas");
    pc.width = canvas.width;
    pc.height = sliceH;
    pc.getContext("2d").drawImage(canvas, 0, rendered, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    if (page > 0) pdf.addPage();
    pdf.addImage(pc.toDataURL("image/png"), "PNG", margins.left, margins.top, imgWidth, (sliceH * imgWidth) / canvas.width);

    rendered = cutY;
    page += 1;
  }

  pdf.save(filename);
};
